import React, { createContext, useContext, useState, useEffect } from "react";
import iziToast from "izitoast";
import "izitoast/dist/css/iziToast.min.css";
import { useCustomerAuth } from "../auth/useCustomerAuth";
import { getCustomerRenderScope, getStoredCustomerData } from "../auth/customerIdentity";

export const COMPARE_SELECTION_KEY = "partywitty:comparison-selection:v1";
const currentOwner = () => getCustomerRenderScope(getStoredCustomerData());
const readOwnedSelection = (owner) => {
  try {
    const stored = JSON.parse(localStorage.getItem(COMPARE_SELECTION_KEY) || "null");
    return stored?.owner === owner && Array.isArray(stored.items)
      ? stored.items.filter((item) => item && typeof item === "object").slice(0, 6)
      : [];
  } catch { return []; }
};

const CompareContext = createContext();

export const useCompare = () => {
  const context = useContext(CompareContext);
  if (!context) {
    throw new Error("useCompare must be used within CompareProvider");
  }
  return context;
};

export const CompareProvider = ({ children }) => {
  const { userData } = useCustomerAuth();
  const owner = getCustomerRenderScope(userData);
  const resolveSource = (item) => {
    const source = String(
      item?.source ||
        item?.bid_source ||
        item?.booking_source ||
        (item?.food_discount !== undefined || item?.alcoholic_discount !== undefined
          ? "table"
          : "package"),
    )
      .trim()
      .toLowerCase();
    if (source === "group" || source === "package") return "package";
    if (source === "table") return "table";
    if (source === "caterer" || source === "catering") return "catering";
    return "package";
  };

  const getCompareKey = (item) => {
    if (!item || typeof item !== "object") return "";
    const source = resolveSource(item);
    const id = String(item?.id ?? "");
    const clubId = String(item?.club_id ?? item?.venue_id ?? "");
    const leadId = String(item?.lead_id ?? "");
    return `${source}:${id}:${clubId}:${leadId}`;
  };

  const matchByKey = (left, right) => {
    const leftKey = getCompareKey(left);
    const rightKey = getCompareKey(right);
    return !!leftKey && leftKey === rightKey;
  };

  const [selection, setSelection] = useState(() => ({ owner, items: readOwnedSelection(owner) }));
  const selectedPackages = selection.owner === owner ? selection.items : [];
  const setSelectedPackages = (update) => {
    if (owner !== currentOwner()) return;
    setSelection((previous) => {
      if (owner !== currentOwner()) return previous;
      const items = previous.owner === owner ? previous.items : [];
      return { owner, items: typeof update === "function" ? update(items) : update };
    });
  };

  useEffect(() => {
    setSelection((previous) => previous.owner === owner ? previous : { owner, items: readOwnedSelection(owner) });
  }, [owner]);

  useEffect(() => {
    if (selection.owner !== owner || owner !== currentOwner()) return;
    try {
      // One atomic owner/selection receipt prevents cross-tab partial restores.
      localStorage.setItem(COMPARE_SELECTION_KEY, JSON.stringify(selection));
      localStorage.removeItem("comparePackages");
    } catch (error) {
      // Silent fail
    }
  }, [owner, selection]);

  const togglePackage = (packageData) => {
setSelectedPackages((prev) => {
const exists = prev.find((p) => matchByKey(p, packageData));
      if (exists) {
const newPackages = prev.filter((p) => !matchByKey(p, packageData));
return newPackages;
      } else {
        if (prev.length >= 6) {
          iziToast.warning({
            title: "Maximum Limit",
            message: "You can compare maximum 6 packages",
            position: "topRight",
            timeout: 3000,
          });
          return prev;
        }
const newPackages = [...prev, packageData];
return newPackages;
      }
    });
  };

  const isSelected = (itemOrId, source = "package") => {
    let selected = false;
    if (itemOrId && typeof itemOrId === "object") {
      selected = selectedPackages.some((p) => matchByKey(p, itemOrId));
    } else {
      const id = String(itemOrId ?? "");
      const normalizedSource = String(source || "package")
        .trim()
        .toLowerCase();
      selected = selectedPackages.some(
        (p) =>
          String(p?.id ?? "") === id &&
          resolveSource(p) ===
            (normalizedSource === "group" ? "package" : normalizedSource),
      );
    }
return selected;
  };

  const clearSelection = () => {
    if (owner !== currentOwner()) return;
setSelectedPackages([]);
    localStorage.removeItem(COMPARE_SELECTION_KEY);
    localStorage.removeItem('comparePackages');
  };
return (
    <CompareContext.Provider
      value={{
        selectedPackages,
        togglePackage,
        isSelected,
        clearSelection,
      }}>
      {children}
    </CompareContext.Provider>
  );
};
